// Workout Templates Page

import { query, run, saveDatabase } from "../db.js";
import { navigateTo, showToast, showConfirmDialog } from "../router.js";

export async function renderTemplatesPage(params) {
  const app = document.getElementById("app");

  const templates = query("SELECT * FROM Template ORDER BY id DESC");

  app.innerHTML = `
    <div class="page safe-top">
      <div class="page-header">
        <div class="page-header-top">
          <h1 class="page-title">Workout Templates</h1>
          <button class="btn-primary btn-sm" id="create-template-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            New
          </button>
        </div>
      </div>

      <div style="padding: 0 var(--spacing-md); margin-bottom: var(--spacing-xl);">
        ${
          templates.length > 0
            ? `
          <div style="display: flex; flex-direction: column; gap: var(--spacing-md);">
            ${templates
              .map((template) => renderTemplateCard(template))
              .join("")}
          </div>
        `
            : `
          <div class="empty-state">
            <svg class="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
            <h3 class="empty-state-title">No Templates Yet</h3>
            <p class="empty-state-description">
              Create your first workout template to get started
            </p>
            <button class="btn-primary" id="empty-create-btn">
              Create Template
            </button>
          </div>
        `
        }
      </div>
    </div>

    <!-- Create Template Modal -->
    <div id="create-modal" class="modal-overlay" style="display: none;">
      <div class="modal">
        <div class="modal-header">
          <h2 class="modal-title">New Template</h2>
          <button class="modal-close" id="close-modal-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div style="margin-bottom: var(--spacing-md);">
            <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
              Template Name
            </label>
            <input type="text" id="template-name" placeholder="e.g., Push Day" autofocus />
          </div>
          <div style="margin-bottom: var(--spacing-md);">
            <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
              Notes (optional)
            </label>
            <textarea id="template-notes" placeholder="Workout description..." maxlength="500"></textarea>
            <p class="text-xs text-muted" style="margin-top: 0.25rem;">
              <span id="notes-count">0</span>/500 characters
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" id="cancel-create-btn">Cancel</button>
          <button class="btn-primary" id="save-template-btn">Create</button>
        </div>
      </div>
    </div>
  `;

  // Event listeners
  const createBtn = document.getElementById("create-template-btn");
  const emptyCreateBtn = document.getElementById("empty-create-btn");
  const modal = document.getElementById("create-modal");
  const closeBtn = document.getElementById("close-modal-btn");
  const cancelBtn = document.getElementById("cancel-create-btn");
  const saveBtn = document.getElementById("save-template-btn");
  const nameInput = document.getElementById("template-name");
  const notesInput = document.getElementById("template-notes");
  const notesCount = document.getElementById("notes-count");

  const openModal = () => {
    modal.style.display = "flex";
    nameInput.value = "";
    notesInput.value = "";
    notesCount.textContent = "0";
    setTimeout(() => nameInput.focus(), 100);
  };

  const closeModal = () => {
    modal.style.display = "none";
  };

  createBtn?.addEventListener("click", openModal);
  emptyCreateBtn?.addEventListener("click", openModal);
  closeBtn?.addEventListener("click", closeModal);
  cancelBtn?.addEventListener("click", closeModal);

  modal?.addEventListener("click", (e) => {
    if (e.target === modal) closeModal();
  });

  notesInput?.addEventListener("input", () => {
    notesCount.textContent = notesInput.value.length;
  });

  saveBtn?.addEventListener("click", async () => {
    const name = nameInput.value.trim();
    const notes = notesInput.value.trim();

    if (!name) {
      showToast("Please enter a template name", "error");
      return;
    }

    try {
      const templateId = run(
        "INSERT INTO Template (name, notes) VALUES (?, ?)",
        [name, notes || null]
      );

      await saveDatabase();
      showToast("Template created successfully", "success");
      closeModal();

      // Navigate to template detail page
      navigateTo(`/templates/${templateId}`);
    } catch (err) {
      console.error("Failed to create template:", err);
      showToast("Failed to create template", "error");
    }
  });

  // Template card click handlers
  document.querySelectorAll("[data-template-id]").forEach((card) => {
    card.addEventListener("click", (e) => {
      if (e.target.closest(".workout-card-actions")) return;

      const templateId = card.getAttribute("data-template-id");
      navigateTo(`/templates/${templateId}`);
    });
  });

  // Start workout buttons
  document.querySelectorAll("[data-start-workout]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const templateId = btn.getAttribute("data-start-workout");
      navigateTo(`/workout/${templateId}`);
    });
  });

  // Clone buttons
  document.querySelectorAll("[data-clone-template]").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const templateId = btn.getAttribute("data-clone-template");

      try {
        await cloneTemplate(templateId);
        showToast("Template cloned successfully", "success");
        renderTemplatesPage();
      } catch (err) {
        console.error("Failed to clone template:", err);
        showToast("Failed to clone template", "error");
      }
    });
  });

  // Delete buttons
  document.querySelectorAll("[data-delete-template]").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const templateId = btn.getAttribute("data-delete-template");
      const template = query("SELECT name FROM Template WHERE id = ?", [templateId])[0];

      const confirmed = await showConfirmDialog(
        `Delete "${template?.name || 'this template'}"?`,
        "Delete"
      );

      if (confirmed) {
        try {
          run("DELETE FROM Template WHERE id = ?", [templateId]);
          await saveDatabase();
          showToast("Template deleted", "success");
          renderTemplatesPage();
        } catch (err) {
          console.error("Failed to delete template:", err);
          showToast("Failed to delete template", "error");
        }
      }
    });
  });
}

async function cloneTemplate(templateId) {
  // Get the original template
  const template = query("SELECT * FROM Template WHERE id = ?", [templateId])[0];

  if (!template) {
    throw new Error("Template not found");
  }

  // Create new template with cloned name
  const clonedTemplateId = run(
    "INSERT INTO Template (name, notes) VALUES (?, ?)",
    [template.name + " - clone", template.notes]
  );

  // Get all exercises from the original template
  const exercises = query(
    "SELECT * FROM PlannedExercises WHERE workout_id = ?",
    [templateId]
  );

  // Clone each exercise and its sets
  for (const exercise of exercises) {
    const clonedExerciseId = run(
      "INSERT INTO PlannedExercises (workout_id, exercise_id, notes) VALUES (?, ?, ?)",
      [clonedTemplateId, exercise.exercise_id, exercise.notes]
    );

    // Get all sets for this exercise
    const sets = query(
      "SELECT * FROM PlannedSets WHERE workout_exercise_id = ?",
      [exercise.id]
    );

    // Clone each set
    for (const set of sets) {
      run(
        "INSERT INTO PlannedSets (workout_exercise_id, weight, reps, duration_seconds) VALUES (?, ?, ?, ?)",
        [clonedExerciseId, set.weight, set.reps, set.duration_seconds]
      );
    }
  }

  await saveDatabase();
  return clonedTemplateId;
}

function renderTemplateCard(template) {
  const exercises = query(
    `
    SELECT we.*, e.name, e.musclegroup
    FROM PlannedExercises we
    JOIN Exercises e ON we.exercise_id = e.id
    WHERE we.workout_id = ?
  `,
    [template.id]
  );

  const totalSets =
    query(
      `
    SELECT COUNT(*) as count
    FROM PlannedSets ps
    JOIN PlannedExercises we ON ps.workout_exercise_id = we.id
    WHERE we.workout_id = ?
  `,
      [template.id]
    )[0]?.count || 0;

  return `
    <div class="workout-card" data-template-id="${template.id}">
      <div class="workout-card-header">
        <h3 class="workout-card-title">${template.name}</h3>
      </div>
      <div class="workout-card-meta">
        <span>${exercises.length} exercises</span>
        <span>${totalSets} sets</span>
      </div>
      ${
        template.notes
          ? `
        <p class="text-sm text-muted" style="margin-top: var(--spacing-sm);">
          ${template.notes}
        </p>
      `
          : ""
      }
      <div class="workout-card-actions">
        <button class="btn-primary" style="flex: 1;" data-start-workout="${
          template.id
        }">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="5 3 19 12 5 21 5 3"></polygon>
          </svg>
          Start
        </button>
        <button class="btn-ghost" data-clone-template="${template.id}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="btn-ghost" data-delete-template="${template.id}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
  `;
}
